import { jsPDF } from "jspdf";
import autoTableImport from "jspdf-autotable";

// jspdf-autotable's CJS/ESM interop differs across bundlers: sometimes the
// default export is the function, sometimes it's a namespace whose `.default`
// is the function. Normalise here so calls always resolve to a function.
const autoTable: (
  doc: jsPDF,
  options: Parameters<typeof autoTableImport>[1],
) => void =
  typeof autoTableImport === "function"
    ? (autoTableImport as never)
    : ((autoTableImport as unknown as { default: typeof autoTableImport })
        .default as never);
import type { MockResults } from "./businessData";
import { getBudgetItemsForType, calculateBudgetTotals } from "./budgetItems";
import { formatCityName } from "./utils";

/**
 * Professional business plan PDF — investor / bank-ready format.
 * Sections: Cover · TOC · Executive Summary · Concept · Market · Location ·
 * Financial Projections (3yr) · Startup Costs · Monthly P&L · Staffing ·
 * Permits · Timeline · Risk Analysis · Appendix.
 */

// ────────────────────────────────────────────────────────────
// User-editable overrides — populated from PlanPreview's editor
// ────────────────────────────────────────────────────────────
export interface PlanOverrides {
  coverTitle?: string;
  coverSubtitle?: string;
  executiveSummary?: string;
  conceptOverview?: string;
  marketAnalysis?: string;
  locationStrategy?: string;
  financialsCommentary?: string;
  startupCostsCommentary?: string;
  pnlCommentary?: string;
  staffingCommentary?: string;
  permitsCommentary?: string;
  timelineCommentary?: string;
  riskCommentary?: string;
  /** Brand customization (logo, primary color, cover hero image) */
  brand?: BrandOverrides;
}

export interface BrandOverrides {
  /** PNG/JPEG data URL — drawn top-left on cover & page header */
  logoDataUrl?: string;
  /** Hex color "#RRGGBB" — replaces the teal accent throughout */
  primaryHex?: string;
  /** PNG/JPEG data URL — full-bleed cover hero (replaces dark banner) */
  coverImageDataUrl?: string;
  /** Optional company / brand name shown on cover & header instead of "oplif." */
  brandName?: string;
}

/**
 * Returns the default prose text the PDF would render for each editable
 * section, so the editor can pre-fill its inputs.
 */
export function getDefaultPlanOverrides(
  results: MockResults,
  lang: "en" | "es" = "en",
): Required<Omit<PlanOverrides, "brand">> {
  const t = lang === "es" ? ES : EN;
  const sym = (results as any).currencySymbol || "$";
  const os = results.openingSummary;
  const cogsPct = 0.30;
  const rentPct = 0.08;
  const opexPct = 0.12;
  return {
    coverTitle: t.title,
    coverSubtitle: t.subtitle,
    executiveSummary: lang === "es"
      ? `Este documento presenta el plan de negocio de ${results.businessLabel.toLowerCase()} ubicado en ${formatCityName(results.city)}. El proyecto requiere una inversión inicial estimada de ${fmtMoneyFull(results.totalEstimate, sym)}, con una preparación operativa actual del ${results.openingScore}/100 y un cronograma de apertura de ${results.timeline}. El plan integra la estrategia de ubicación, proyecciones financieras a tres años, plan de personal, requisitos regulatorios y análisis de riesgos, conforme a las prácticas estándar de la industria de hostelería y comercio minorista.`
      : `This document presents the business plan for the ${results.businessLabel.toLowerCase()} project located in ${formatCityName(results.city)}. The venture requires an estimated initial investment of ${fmtMoneyFull(results.totalEstimate, sym)}, currently scores ${results.openingScore}/100 on operational readiness, and follows a launch timeline of ${results.timeline}. This plan integrates location strategy, three-year financial projections, staffing, regulatory requirements and risk analysis aligned with hospitality and retail industry standards.`,
    conceptOverview: lang === "es"
      ? `${results.businessLabel} se posicionará como un establecimiento ${os.serviceStyle.toLowerCase()} en ${formatCityName(results.city)}, atendiendo a clientes locales y visitantes. El concepto se construye en torno a una experiencia consistente, operaciones eficientes y una propuesta de valor diferenciada por calidad y servicio.`
      : `${results.businessLabel} will position itself as a ${os.serviceStyle.toLowerCase()} establishment in ${formatCityName(results.city)}, serving local customers and visitors. The concept is built around a consistent guest experience, efficient operations, and a differentiated value proposition centered on quality and service.`,
    marketAnalysis: lang === "es"
      ? `${formatCityName(results.city)} representa un mercado activo para conceptos de ${results.businessLabel.toLowerCase()}. El análisis de barrios identifica zonas con tráfico, niveles de renta y ajuste para el concepto. Las puntuaciones reflejan accesibilidad, demografía y competencia local.`
      : `${formatCityName(results.city)} presents an active market for ${results.businessLabel.toLowerCase()} concepts. The neighborhood analysis below evaluates foot traffic, rent levels, and concept fit. Scores reflect accessibility, demographics, and local competition.`,
    locationStrategy: lang === "es"
      ? `La estrategia de ubicación da prioridad a la visibilidad, accesibilidad y proximidad a la base de clientes objetivo. La superficie recomendada se ajusta al modelo operativo y a la capacidad prevista.`
      : `The location strategy prioritizes visibility, accessibility and proximity to the target customer base. Recommended floor area is sized to the operating model and projected capacity.`,
    financialsCommentary: lang === "es"
      ? `Las proyecciones a tres años se modelan a partir de costos laborales conocidos y referencias del sector (COGS ~${(cogsPct * 100).toFixed(0)}%, renta ~${(rentPct * 100).toFixed(0)}%, opex ~${(opexPct * 100).toFixed(0)}%). El Año 1 refleja una rampa al 75% de la capacidad estabilizada; el Año 2 alcanza la madurez; el Año 3 incluye crecimiento del 15%.`
      : `Three-year projections are modeled from known labor costs and industry benchmarks (COGS ~${(cogsPct * 100).toFixed(0)}%, rent ~${(rentPct * 100).toFixed(0)}%, opex ~${(opexPct * 100).toFixed(0)}%). Year 1 reflects a ramp to 75% of stabilized run-rate; Year 2 reaches maturity; Year 3 incorporates 15% growth.`,
    startupCostsCommentary: lang === "es"
      ? `El capital inicial total estimado asciende a ${fmtMoneyFull(results.totalEstimate, sym)}. El siguiente desglose muestra la asignación por categoría según referencias del sector y el tipo de negocio.`
      : `Total estimated startup capital is ${fmtMoneyFull(results.totalEstimate, sym)}. The breakdown below shows allocation by category, derived from industry benchmarks and the selected business type.`,
    pnlCommentary: lang === "es"
      ? `Pronóstico mensual del Año 1 mostrando la rampa hasta el funcionamiento estabilizado. El break-even se proyecta entre los meses 6 y 9 según la velocidad de adquisición de clientes.`
      : `Year 1 monthly forecast showing the ramp to stabilized run-rate. Break-even is projected between months 6 and 9 depending on customer acquisition pace.`,
    staffingCommentary: lang === "es"
      ? `El plan de personal cubre los roles esenciales para operar de forma segura y eficiente. Las tarifas mensuales se basan en referencias del mercado local y suposiciones estándar de horas (40h/semana para tiempo completo).`
      : `The staffing plan covers all roles required for safe and efficient operations. Monthly rates are based on local market benchmarks and standard hour assumptions (40 hrs/week for full-time roles).`,
    permitsCommentary: lang === "es"
      ? `Lista regulatoria para ${formatCityName(results.city)}. Cada permiso incluye su tiempo estimado de procesamiento y costo de referencia. Confirme con cada autoridad antes de presupuestar.`
      : `Regulatory checklist for ${formatCityName(results.city)}. Each permit includes its estimated processing time and reference cost. Confirm requirements with each issuing authority before finalizing budgets.`,
    timelineCommentary: lang === "es"
      ? `Cronograma estructurado por fases hasta la apertura. Las dependencias críticas se agrupan al inicio del proceso para evitar retrasos en cascada.`
      : `Phased timeline through opening day. Critical-path dependencies are front-loaded to prevent cascading delays.`,
    riskCommentary: lang === "es"
      ? `Identificación de los principales riesgos del proyecto y estrategias de mitigación. La probabilidad e impacto se evalúan según el patrón histórico del sector.`
      : `Identification of key project risks and mitigation strategies. Likelihood and impact are scored against historical industry patterns.`,
  };
}

// ────────────────────────────────────────────────────────────
// Translations (kept minimal — labels reused across sections)
// ────────────────────────────────────────────────────────────
interface Strings {
  title: string;
  subtitle: string;
  preparedBy: string;
  preparedFor: string;
  date: string;
  confidential: string;
  page: string;
  of: string;
  toc: string;
  // sections
  s_exec: string;
  s_concept: string;
  s_market: string;
  s_location: string;
  s_financials: string;
  s_startup: string;
  s_pnl: string;
  s_staffing: string;
  s_permits: string;
  s_timeline: string;
  s_risk: string;
  s_appendix: string;
  // misc
  disclaimer: string;
  totalLabel: string;
  year: string;
  month: string;
  notes: string;
}

const EN: Strings = {
  title: "Business Plan",
  subtitle: "Comprehensive Launch & Operations Strategy",
  preparedBy: "Prepared by Oplif",
  preparedFor: "Prepared for",
  date: "Date",
  confidential: "CONFIDENTIAL — Prepared by Oplif · oplif.com",
  page: "Page",
  of: "of",
  toc: "Table of Contents",
  s_exec: "1. Executive Summary",
  s_concept: "2. Concept Overview",
  s_market: "3. Market Analysis",
  s_location: "4. Location Strategy",
  s_financials: "5. Financial Projections (3 Years)",
  s_startup: "6. Startup Cost Breakdown",
  s_pnl: "7. Monthly P&L Forecast (Year 1)",
  s_staffing: "8. Staffing Plan & Salary Breakdown",
  s_permits: "9. Permit & Compliance Checklist",
  s_timeline: "10. Timeline to Opening",
  s_risk: "11. Risk Analysis & Mitigation",
  s_appendix: "12. Appendix — Detailed Startup Budget",
  disclaimer:
    "Disclaimer: Projections are model estimates based on user inputs, regional benchmarks and industry averages. Actual results may vary materially. Independently verify with local authorities, accountants and qualified advisors before making financial commitments.",
  totalLabel: "Total",
  year: "Year",
  month: "Month",
  notes: "Notes",
};

const ES: Strings = {
  title: "Plan de Negocio",
  subtitle: "Estrategia Integral de Lanzamiento y Operaciones",
  preparedBy: "Preparado por Oplif",
  preparedFor: "Preparado para",
  date: "Fecha",
  confidential: "CONFIDENCIAL — Preparado por Oplif · oplif.com",
  page: "Página",
  of: "de",
  toc: "Índice",
  s_exec: "1. Resumen Ejecutivo",
  s_concept: "2. Visión del Concepto",
  s_market: "3. Análisis de Mercado",
  s_location: "4. Estrategia de Ubicación",
  s_financials: "5. Proyecciones Financieras (3 Años)",
  s_startup: "6. Desglose de Costos Iniciales",
  s_pnl: "7. Pronóstico Mensual P&L (Año 1)",
  s_staffing: "8. Plan de Personal y Salarios",
  s_permits: "9. Permisos y Cumplimiento",
  s_timeline: "10. Cronograma hasta la Apertura",
  s_risk: "11. Análisis de Riesgos y Mitigación",
  s_appendix: "12. Apéndice — Presupuesto Detallado",
  disclaimer:
    "Aviso: Las proyecciones son estimaciones basadas en datos del usuario y referencias del sector. Los resultados reales pueden variar. Verifique con autoridades locales y asesores cualificados antes de tomar decisiones financieras.",
  totalLabel: "Total",
  year: "Año",
  month: "Mes",
  notes: "Notas",
};

// ────────────────────────────────────────────────────────────
// Brand palette (Oplif)
// ────────────────────────────────────────────────────────────
const PRIMARY: [number, number, number] = [13, 148, 136]; // teal-600 (default brand)
const PRIMARY_DARK: [number, number, number] = [15, 118, 110];
const INK: [number, number, number] = [15, 23, 42]; // slate-900
const SUBINK: [number, number, number] = [51, 65, 85]; // slate-700
const MUTED: [number, number, number] = [100, 116, 139]; // slate-500
const HAIR: [number, number, number] = [226, 232, 240]; // slate-200
const SOFT_BG: [number, number, number] = [248, 250, 252]; // slate-50
const ACCENT: [number, number, number] = [234, 88, 12]; // orange-600
const GREEN: [number, number, number] = [22, 163, 74];
const RED: [number, number, number] = [220, 38, 38];

function hexToRgb(hex?: string): [number, number, number] | null {
  if (!hex) return null;
  const m = hex.trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(m)) return null;
  return [
    parseInt(m.slice(0, 2), 16),
    parseInt(m.slice(2, 4), 16),
    parseInt(m.slice(4, 6), 16),
  ];
}

function darken(rgb: [number, number, number], amt = 0.15): [number, number, number] {
  return [
    Math.max(0, Math.round(rgb[0] * (1 - amt))),
    Math.max(0, Math.round(rgb[1] * (1 - amt))),
    Math.max(0, Math.round(rgb[2] * (1 - amt))),
  ];
}

// Bar chart palette
const CHART_COLORS: Array<[number, number, number]> = [
  [13, 148, 136],
  [59, 130, 246],
  [234, 88, 12],
  [168, 85, 247],
  [22, 163, 74],
  [234, 179, 8],
  [236, 72, 153],
  [99, 102, 241],
  [20, 184, 166],
];

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────
function fmtMoney(n: number, sym = "$"): string {
  if (!isFinite(n)) return `${sym}0`;
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${sym}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 10_000) return `${sign}${sym}${(abs / 1_000).toFixed(0)}K`;
  if (abs >= 1_000) return `${sign}${sym}${(abs / 1_000).toFixed(1)}K`;
  return `${sign}${sym}${Math.round(abs).toLocaleString()}`;
}

function fmtMoneyFull(n: number, sym = "$"): string {
  return `${sym}${Math.round(n).toLocaleString()}`;
}

function pct(n: number): string {
  return `${n.toFixed(1)}%`;
}

// ────────────────────────────────────────────────────────────
// Build a section registry for TOC + outline
// ────────────────────────────────────────────────────────────
interface TocEntry {
  title: string;
  page: number;
}

function buildPDF(
  results: MockResults,
  lang: "en" | "es" = "en",
  businessType?: string,
  overrides: PlanOverrides = {},
): jsPDF {
  const t = lang === "es" ? ES : EN;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const PAGE_H = doc.internal.pageSize.getHeight();
  const M = 18; // outer margin
  const CW = PAGE_W - M * 2;
  const sym = (results as any).currencySymbol || "$";
  const toc: TocEntry[] = [];
  let y = 0;

  // ── Brand resolution (per-document; shadows module defaults) ──
  const brand = overrides.brand ?? {};
  const PRIMARY: [number, number, number] = hexToRgb(brand.primaryHex) ?? [13, 148, 136];
  const PRIMARY_DARK: [number, number, number] = darken(PRIMARY, 0.18);
  const brandName = brand.brandName?.trim() || "oplif.";
  const logoImage = brand.logoDataUrl;
  const coverImage = brand.coverImageDataUrl;

  function detectImgFormat(dataUrl: string): "PNG" | "JPEG" {
    return /^data:image\/jpe?g/i.test(dataUrl) ? "JPEG" : "PNG";
  }

  function safeAddImage(
    dataUrl: string,
    x: number,
    yPos: number,
    w: number,
    h: number,
  ): boolean {
    try {
      doc.addImage(dataUrl, detectImgFormat(dataUrl), x, yPos, w, h, undefined, "FAST");
      return true;
    } catch (e) {
      console.warn("PDF: failed to embed image", e);
      return false;
    }
  }

  // ── primitives ────────────────────────────────────────────
  function setFont(size: number, weight: "normal" | "bold" = "normal", color: [number, number, number] = INK) {
    doc.setFontSize(size);
    doc.setFont("helvetica", weight);
    doc.setTextColor(...color);
  }

  function checkPage(needed: number) {
    if (y + needed > PAGE_H - 22) {
      doc.addPage();
      drawPageChrome();
      y = M + 14;
    }
  }

  function drawPageChrome() {
    // top brand bar (thin)
    doc.setFillColor(...PRIMARY);
    doc.rect(0, 0, PAGE_W, 4, "F");
    // left: logo (if provided) or brand name
    if (logoImage) {
      safeAddImage(logoImage, M, 6.5, 14, 5.5);
    } else {
      setFont(8, "normal", MUTED);
      doc.text(brandName, M, 11);
    }
    // right: business · city
    setFont(8, "normal", MUTED);
    doc.text(
      `${results.businessLabel} · ${formatCityName(results.city)}`,
      PAGE_W - M,
      11,
      { align: "right" },
    );
    // hairline
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.2);
    doc.line(M, 14, PAGE_W - M, 14);
  }

  function sectionHeader(title: string) {
    // record TOC entry on the page that will hold the section
    // (caller starts a new page first)
    toc.push({ title, page: doc.getNumberOfPages() });
    setFont(18, "bold", INK);
    doc.text(title, M, y);
    y += 3;
    doc.setDrawColor(...PRIMARY);
    doc.setLineWidth(0.8);
    doc.line(M, y, M + 22, y);
    y += 9;
  }

  function subHeader(title: string) {
    checkPage(12);
    y += 2;
    setFont(11.5, "bold", PRIMARY_DARK);
    doc.text(title, M, y);
    y += 5;
  }

  function paragraph(text: string, size = 10) {
    setFont(size, "normal", SUBINK);
    const lines = doc.splitTextToSize(text, CW);
    for (const line of lines) {
      checkPage(size * 0.45 + 1.5);
      doc.text(line, M, y);
      y += size * 0.45 + 1.5;
    }
    y += 1.5;
  }

  function bullet(text: string) {
    setFont(10, "normal", SUBINK);
    const lines = doc.splitTextToSize(text, CW - 6);
    checkPage(lines.length * 5 + 1);
    doc.setFillColor(...PRIMARY);
    doc.circle(M + 1.5, y - 1.4, 0.9, "F");
    doc.text(lines, M + 5, y);
    y += lines.length * 5 + 1;
  }

  function startSectionPage() {
    doc.addPage();
    drawPageChrome();
    y = M + 14;
  }

  // ── KPI cards ─────────────────────────────────────────────
  function kpiCards(cards: Array<{ label: string; value: string; sub?: string }>) {
    const gap = 4;
    const w = (CW - gap * (cards.length - 1)) / cards.length;
    const h = 22;
    checkPage(h + 4);
    cards.forEach((c, i) => {
      const x = M + i * (w + gap);
      doc.setFillColor(...SOFT_BG);
      doc.setDrawColor(...HAIR);
      doc.setLineWidth(0.2);
      doc.roundedRect(x, y, w, h, 1.5, 1.5, "FD");
      setFont(7.5, "normal", MUTED);
      doc.text(c.label.toUpperCase(), x + 3, y + 5);
      setFont(13, "bold", INK);
      doc.text(c.value, x + 3, y + 13);
      if (c.sub) {
        setFont(7.5, "normal", MUTED);
        doc.text(c.sub, x + 3, y + 18);
      }
    });
    y += h + 4;
  }

  // ── Bar chart (vertical) ──────────────────────────────────
  function barChart(
    data: Array<{ label: string; value: number; color?: [number, number, number] }>,
    opts: { height?: number; yAxisLabel?: string } = {},
  ) {
    const h = opts.height ?? 55;
    const padTop = 6;
    const padBottom = 14;
    const padLeft = 18;
    const chartW = CW - padLeft - 2;
    const chartH = h - padTop - padBottom;
    checkPage(h + 6);

    const maxV = Math.max(1, ...data.map(d => d.value));
    // axis
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.2);
    doc.line(M + padLeft, y + padTop, M + padLeft, y + padTop + chartH);
    doc.line(
      M + padLeft,
      y + padTop + chartH,
      M + padLeft + chartW,
      y + padTop + chartH,
    );
    // y grid (4 lines)
    setFont(6.5, "normal", MUTED);
    for (let i = 0; i <= 4; i++) {
      const gy = y + padTop + chartH - (chartH * i) / 4;
      doc.setDrawColor(240, 244, 248);
      doc.line(M + padLeft, gy, M + padLeft + chartW, gy);
      doc.setTextColor(...MUTED);
      doc.text(fmtMoney((maxV * i) / 4, sym), M + padLeft - 1, gy + 1.2, { align: "right" });
    }
    // bars
    const slot = chartW / data.length;
    const barW = Math.min(18, slot * 0.6);
    data.forEach((d, i) => {
      const bh = (d.value / maxV) * chartH;
      const bx = M + padLeft + slot * i + (slot - barW) / 2;
      const by = y + padTop + chartH - bh;
      const color = d.color ?? CHART_COLORS[i % CHART_COLORS.length];
      doc.setFillColor(...color);
      doc.rect(bx, by, barW, bh, "F");
      // value
      setFont(6.5, "bold", INK);
      doc.text(fmtMoney(d.value, sym), bx + barW / 2, by - 1.2, { align: "center" });
      // label
      setFont(7, "normal", SUBINK);
      const lbl = doc.splitTextToSize(d.label, slot - 1);
      doc.text(lbl[0], bx + barW / 2, y + padTop + chartH + 4, { align: "center" });
      if (lbl[1])
        doc.text(lbl[1], bx + barW / 2, y + padTop + chartH + 7.5, { align: "center" });
    });
    y += h;
  }

  // ── Donut/pie chart with legend ───────────────────────────
  function donutChart(
    data: Array<{ label: string; value: number }>,
    opts: { size?: number } = {},
  ) {
    const size = opts.size ?? 50;
    const total = data.reduce((s, d) => s + d.value, 0) || 1;
    checkPage(size + 6);

    const cx = M + size / 2 + 2;
    const cy = y + size / 2;
    const r = size / 2;
    const inner = r * 0.55;

    let startAngle = -Math.PI / 2;
    data.forEach((d, i) => {
      const sweep = (d.value / total) * Math.PI * 2;
      const color = CHART_COLORS[i % CHART_COLORS.length];
      doc.setFillColor(...color);
      // Approximate pie slice with triangles fan
      const steps = Math.max(6, Math.ceil((sweep / (Math.PI * 2)) * 60));
      for (let s = 0; s < steps; s++) {
        const a1 = startAngle + (sweep * s) / steps;
        const a2 = startAngle + (sweep * (s + 1)) / steps;
        doc.triangle(
          cx,
          cy,
          cx + Math.cos(a1) * r,
          cy + Math.sin(a1) * r,
          cx + Math.cos(a2) * r,
          cy + Math.sin(a2) * r,
          "F",
        );
      }
      startAngle += sweep;
    });
    // donut hole
    doc.setFillColor(255, 255, 255);
    doc.circle(cx, cy, inner, "F");
    setFont(7, "normal", MUTED);
    doc.text(t.totalLabel.toUpperCase(), cx, cy - 1, { align: "center" });
    setFont(10, "bold", INK);
    doc.text(fmtMoney(total, sym), cx, cy + 4, { align: "center" });

    // legend
    const legendX = M + size + 10;
    const legendW = CW - size - 12;
    let ly = y + 2;
    setFont(8.5, "normal", SUBINK);
    data.forEach((d, i) => {
      const color = CHART_COLORS[i % CHART_COLORS.length];
      doc.setFillColor(...color);
      doc.rect(legendX, ly - 2.4, 3, 3, "F");
      const pctVal = ((d.value / total) * 100).toFixed(1);
      const txt = `${d.label}`;
      const right = `${fmtMoney(d.value, sym)} · ${pctVal}%`;
      setFont(8.5, "normal", SUBINK);
      doc.text(txt, legendX + 5, ly);
      setFont(8.5, "bold", INK);
      doc.text(right, legendX + legendW, ly, { align: "right" });
      ly += 5.2;
    });
    y += Math.max(size, ly - y);
    y += 3;
  }

  // ────────────────────────────────────────────────────────
  // 0. COVER PAGE
  // ────────────────────────────────────────────────────────
  // Background
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");
  const heroH = PAGE_H * 0.42;

  // Hero band: custom cover image OR dark INK banner
  const heroImageRendered = coverImage && safeAddImage(coverImage, 0, 0, PAGE_W, heroH);
  if (!heroImageRendered) {
    doc.setFillColor(...INK);
    doc.rect(0, 0, PAGE_W, heroH, "F");
  } else {
    // Subtle dark scrim so white text remains legible on top of the image
    doc.setFillColor(0, 0, 0);
    (doc as any).setGState?.(new (doc as any).GState({ opacity: 0.45 }));
    doc.rect(0, 0, PAGE_W, heroH, "F");
    (doc as any).setGState?.(new (doc as any).GState({ opacity: 1 }));
  }
  // brand stripe
  doc.setFillColor(...PRIMARY);
  doc.rect(0, heroH, PAGE_W, 1.4, "F");

  // Top-left: logo OR brand name
  if (logoImage) {
    safeAddImage(logoImage, M, 16, 28, 11);
  } else {
    setFont(11, "bold", [255, 255, 255]);
    doc.text(brandName, M, 22);
  }
  setFont(8, "normal", [200, 220, 230]);
  doc.text(t.preparedBy, PAGE_W - M, 22, { align: "right" });

  setFont(34, "bold", [255, 255, 255]);
  doc.text(overrides.coverTitle?.trim() || t.title, M, PAGE_H * 0.28);
  setFont(13, "normal", [220, 235, 240]);
  doc.text(overrides.coverSubtitle?.trim() || t.subtitle, M, PAGE_H * 0.28 + 9);

  setFont(10, "normal", [200, 220, 230]);
  doc.text(results.businessLabel, M, PAGE_H * 0.36);
  doc.text(formatCityName(results.city), M, PAGE_H * 0.36 + 5);

  // Lower meta block
  const metaY = PAGE_H * 0.55;
  setFont(8.5, "normal", MUTED);
  doc.text(t.preparedFor.toUpperCase(), M, metaY);
  setFont(13, "bold", INK);
  doc.text(results.businessLabel, M, metaY + 6);
  setFont(10, "normal", SUBINK);
  doc.text(formatCityName(results.city), M, metaY + 12);

  setFont(8.5, "normal", MUTED);
  doc.text(t.date.toUpperCase(), M, metaY + 22);
  setFont(11, "normal", INK);
  doc.text(
    new Date().toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }),
    M,
    metaY + 28,
  );

  // Cover KPIs
  const cardsY = PAGE_H * 0.72;
  const cards = [
    { label: "STARTUP CAPITAL", value: fmtMoney(results.totalEstimate, sym) },
    { label: "READINESS SCORE", value: `${results.openingScore}/100` },
    { label: "TIMELINE", value: results.timeline },
  ];
  const cw = (CW - 8) / 3;
  cards.forEach((c, i) => {
    const x = M + i * (cw + 4);
    doc.setFillColor(...SOFT_BG);
    doc.setDrawColor(...HAIR);
    doc.roundedRect(x, cardsY, cw, 22, 1.5, 1.5, "FD");
    setFont(7.5, "normal", MUTED);
    doc.text(c.label, x + 3, cardsY + 6);
    setFont(14, "bold", INK);
    doc.text(c.value, x + 3, cardsY + 15);
  });

  // Footer ribbon
  setFont(8, "normal", MUTED);
  doc.text(t.confidential, M, PAGE_H - 12);
  doc.text("oplif.com", PAGE_W - M, PAGE_H - 12, { align: "right" });

  // ────────────────────────────────────────────────────────
  // 1. TABLE OF CONTENTS (placeholder — filled at end)
  // ────────────────────────────────────────────────────────
  doc.addPage();
  drawPageChrome();
  y = M + 14;
  setFont(18, "bold", INK);
  doc.text(t.toc, M, y);
  y += 3;
  doc.setDrawColor(...PRIMARY);
  doc.setLineWidth(0.8);
  doc.line(M, y, M + 22, y);
  y += 10;
  const tocPageNumber = doc.getNumberOfPages();

  // ────────────────────────────────────────────────────────
  // 2. EXECUTIVE SUMMARY
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_exec);

  const os = results.openingSummary;
  const concept = lang === "es"
    ? `Este documento presenta el plan de negocio de ${results.businessLabel.toLowerCase()} ubicado en ${formatCityName(results.city)}. El proyecto requiere una inversión inicial estimada de ${fmtMoneyFull(results.totalEstimate, sym)}, con una preparación operativa actual del ${results.openingScore}/100 y un cronograma de apertura de ${results.timeline}. El plan integra la estrategia de ubicación, proyecciones financieras a tres años, plan de personal, requisitos regulatorios y análisis de riesgos, conforme a las prácticas estándar de la industria de hostelería y comercio minorista.`
    : `This document presents the business plan for the ${results.businessLabel.toLowerCase()} project located in ${formatCityName(results.city)}. The venture requires an estimated initial investment of ${fmtMoneyFull(results.totalEstimate, sym)}, currently scores ${results.openingScore}/100 on operational readiness, and follows a launch timeline of ${results.timeline}. This plan integrates location strategy, three-year financial projections, staffing, regulatory requirements and risk analysis aligned with hospitality and retail industry standards.`;
  paragraph(overrides.executiveSummary?.trim() || concept);

  kpiCards([
    { label: "Total Investment", value: fmtMoney(results.totalEstimate, sym), sub: results.budgetLabel },
    { label: "Readiness Score", value: `${results.openingScore}/100`, sub: results.budgetFit },
    { label: "Time to Open", value: results.timeline, sub: `~${os.totalWeeks} weeks` },
    { label: "Team Size", value: `${results.staffing.length}`, sub: `${fmtMoney(results.totalMonthlyLabor, sym)}/mo` },
  ]);

  subHeader(lang === "es" ? "Aspectos Destacados" : "Key Highlights");
  bullet(
    lang === "es"
      ? `Concepto: ${results.businessLabel} con servicio ${os.serviceStyle.toLowerCase()} en ${os.sqft}.`
      : `Concept: ${results.businessLabel} operating ${os.serviceStyle.toLowerCase()} in ${os.sqft} of space.`,
  );
  bullet(
    lang === "es"
      ? `Capacidad: ${os.seats > 0 ? `${os.seats} asientos` : "sin asientos"}, ${os.daysPerWeek} días/semana, horario ${os.hours}.`
      : `Capacity: ${os.seats > 0 ? `${os.seats} seats` : "no seating"}, operating ${os.daysPerWeek} days/week with hours ${os.hours}.`,
  );
  bullet(
    lang === "es"
      ? `Equipo: ${os.team}. Costo laboral mensual estimado ${fmtMoney(results.totalMonthlyLabor, sym)}.`
      : `Team: ${os.team}. Estimated monthly labor cost ${fmtMoney(results.totalMonthlyLabor, sym)}.`,
  );
  bullet(
    lang === "es"
      ? `Ajuste presupuestario: ${results.budgetFit} frente al presupuesto declarado de ${results.budgetLabel}.`
      : `Budget fit: ${results.budgetFit} against the declared budget of ${results.budgetLabel}.`,
  );
  bullet(
    lang === "es"
      ? `Fecha objetivo de apertura: ${os.targetDate ? new Date(os.targetDate).toLocaleDateString("es-ES", { month: "long", year: "numeric" }) : "Flexible"}.`
      : `Target opening date: ${os.targetDate ? new Date(os.targetDate).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Flexible"}.`,
  );

  // ────────────────────────────────────────────────────────
  // 3. CONCEPT OVERVIEW
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_concept);

  paragraph(
    overrides.conceptOverview?.trim() ||
    (lang === "es"
      ? `${results.businessLabel} se posicionará como un establecimiento ${os.serviceStyle.toLowerCase()} en ${formatCityName(results.city)}, atendiendo a clientes locales y visitantes. El concepto se construye en torno a una experiencia consistente, operaciones eficientes y una propuesta de valor diferenciada por calidad y servicio.`
      : `${results.businessLabel} will position itself as a ${os.serviceStyle.toLowerCase()} establishment in ${formatCityName(results.city)}, serving local customers and visitors. The concept is built around a consistent guest experience, efficient operations, and a differentiated value proposition centered on quality and service.`),
  );

  subHeader(lang === "es" ? "Resumen Operativo" : "Operating Summary");
  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    theme: "plain",
    body: [
      [lang === "es" ? "Tipo de negocio" : "Business type", os.businessType],
      [lang === "es" ? "Ubicación" : "Location", formatCityName(os.city)],
      [lang === "es" ? "Estilo de servicio" : "Service style", os.serviceStyle],
      [lang === "es" ? "Asientos" : "Seating", os.seats > 0 ? `${os.seats} (${os.seating})` : "—"],
      [lang === "es" ? "Superficie" : "Floor area", os.sqft],
      [lang === "es" ? "Horario" : "Hours of operation", `${os.hours} · ${os.daysPerWeek} ${lang === "es" ? "días/sem" : "days/wk"}`],
      [lang === "es" ? "Equipo" : "Team configuration", os.team],
      [lang === "es" ? "Apertura objetivo" : "Target opening", os.targetDate ? new Date(os.targetDate).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", { month: "long", year: "numeric" }) : "Flexible"],
    ],
    styles: { fontSize: 9.5, cellPadding: 3, textColor: SUBINK },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 60, textColor: MUTED }, 1: { cellWidth: CW - 60 } },
    didParseCell: data => {
      if (data.row.index % 2 === 0) data.cell.styles.fillColor = SOFT_BG;
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // ────────────────────────────────────────────────────────
  // 4. MARKET ANALYSIS
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_market);

  paragraph(
    overrides.marketAnalysis?.trim() ||
    (lang === "es"
      ? `${formatCityName(results.city)} representa un mercado activo para conceptos de ${results.businessLabel.toLowerCase()}. El análisis de barrios identifica zonas con tráfico, niveles de renta y ajuste para el concepto. Las puntuaciones reflejan accesibilidad, demografía y competencia local.`
      : `${formatCityName(results.city)} presents an active market for ${results.businessLabel.toLowerCase()} concepts. The neighborhood analysis below evaluates foot traffic, rent levels, and concept fit. Scores reflect accessibility, demographics, and local competition.`),
  );

  if (results.neighborhoods?.length) {
    subHeader(lang === "es" ? "Análisis de Barrios" : "Neighborhood Analysis");
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[
        lang === "es" ? "Barrio" : "Neighborhood",
        lang === "es" ? "Renta" : "Rent range",
        lang === "es" ? "Tráfico" : "Foot traffic",
        lang === "es" ? "Ajuste" : "Concept fit",
      ]],
      body: results.neighborhoods.map(n => [
        n.name,
        n.rentRange,
        n.trafficLevel,
        `${n.fitScore}/100`,
      ]),
      theme: "grid",
      headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2.8, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1 },
      alternateRowStyles: { fillColor: SOFT_BG },
      columnStyles: { 3: { halign: "center", fontStyle: "bold" } },
    });
    y = (doc as any).lastAutoTable.finalY + 6;

    // Bar chart of fit scores
    subHeader(lang === "es" ? "Puntuaciones de Ajuste" : "Concept Fit Scores");
    barChart(
      results.neighborhoods.slice(0, 6).map(n => ({ label: n.name, value: n.fitScore })),
      { height: 50 },
    );
  }

  // ────────────────────────────────────────────────────────
  // 5. LOCATION STRATEGY
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_location);

  paragraph(
    overrides.locationStrategy?.trim() ||
    (lang === "es"
      ? `La estrategia de ubicación da prioridad a la visibilidad, accesibilidad y proximidad a la base de clientes objetivo. La superficie recomendada se ajusta al modelo operativo y a la capacidad prevista.`
      : `The location strategy prioritizes visibility, accessibility and proximity to the target customer base. Recommended floor area is sized to the operating model and projected capacity.`),
  );

  subHeader(lang === "es" ? "Recomendación de Espacio" : "Space Recommendation");
  setFont(11, "bold", INK);
  doc.text(
    `${results.recommendedSqft.min.toLocaleString()}–${results.recommendedSqft.max.toLocaleString()} sq ft`,
    M,
    y,
  );
  y += 5;
  paragraph(results.recommendedSqft.note, 9.5);

  if (results.leaseListings?.length) {
    subHeader(lang === "es" ? "Espacios Comparables Disponibles" : "Comparable Available Spaces");
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[
        lang === "es" ? "Dirección" : "Address",
        lang === "es" ? "Tamaño" : "Size",
        lang === "es" ? "Renta" : "Rent",
        lang === "es" ? "Tipo" : "Type",
      ]],
      body: results.leaseListings.slice(0, 8).map((l: any) => [
        l.address || l.name || "—",
        l.sqft ? `${l.sqft.toLocaleString()} sq ft` : "—",
        l.pricePerMonth
          ? fmtMoneyFull(l.pricePerMonth, sym) + "/mo"
          : l.monthlyRent
            ? fmtMoneyFull(l.monthlyRent, sym) + "/mo"
            : "—",
        l.type || l.spaceType || "—",
      ]),
      theme: "grid",
      headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 8.5, cellPadding: 2.5, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1 },
      alternateRowStyles: { fillColor: SOFT_BG },
    });
    y = (doc as any).lastAutoTable.finalY + 4;
  }

  // ────────────────────────────────────────────────────────
  // 6. FINANCIAL PROJECTIONS — 3 YEAR
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_financials);

  // Build derived projections from available signals
  // Conservative model: monthly revenue derived from labor cost ratio (~28% target)
  const monthlyLabor = results.totalMonthlyLabor || 12000;
  const targetLaborPct = 0.28;
  const baseMonthlyRevenue = Math.max(monthlyLabor / targetLaborPct, 25000);
  // Year ramp: 75% / 100% / 115%
  const ramp = [0.75, 1.0, 1.15];
  const cogsPct = 0.30;
  const rentPct = 0.08;
  const opexPct = 0.12;
  const marketingPct = 0.04;

  const yearProj = ramp.map((r, idx) => {
    const rev = baseMonthlyRevenue * 12 * r;
    const cogs = rev * cogsPct;
    const labor = monthlyLabor * 12 * (idx === 0 ? 0.95 : idx === 1 ? 1.05 : 1.1);
    const rent = rev * rentPct;
    const opex = rev * opexPct;
    const marketing = rev * marketingPct;
    const totalCost = cogs + labor + rent + opex + marketing;
    const ebitda = rev - totalCost;
    return { rev, cogs, labor, rent, opex, marketing, totalCost, ebitda };
  });

  paragraph(
    overrides.financialsCommentary?.trim() ||
    (lang === "es"
      ? `Las proyecciones a tres años se modelan a partir de costos laborales conocidos y referencias del sector (COGS ~${(cogsPct * 100).toFixed(0)}%, renta ~${(rentPct * 100).toFixed(0)}%, opex ~${(opexPct * 100).toFixed(0)}%). El Año 1 refleja una rampa al 75% de la capacidad estabilizada; el Año 2 alcanza la madurez; el Año 3 incluye crecimiento del 15%.`
      : `Three-year projections are modeled from known labor costs and industry benchmarks (COGS ~${(cogsPct * 100).toFixed(0)}%, rent ~${(rentPct * 100).toFixed(0)}%, opex ~${(opexPct * 100).toFixed(0)}%). Year 1 reflects a ramp to 75% of stabilized run-rate; Year 2 reaches maturity; Year 3 incorporates 15% growth.`),
  );

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      lang === "es" ? "Línea" : "Line item",
      "Year 1",
      "Year 2",
      "Year 3",
    ]],
    body: [
      [lang === "es" ? "Ingresos" : "Revenue", ...yearProj.map(p => fmtMoneyFull(p.rev, sym))],
      [lang === "es" ? "Costo de bienes (COGS)" : "Cost of goods sold (COGS)", ...yearProj.map(p => `(${fmtMoneyFull(p.cogs, sym)})`)],
      [lang === "es" ? "Personal" : "Labor", ...yearProj.map(p => `(${fmtMoneyFull(p.labor, sym)})`)],
      [lang === "es" ? "Renta" : "Rent", ...yearProj.map(p => `(${fmtMoneyFull(p.rent, sym)})`)],
      [lang === "es" ? "Gastos operativos" : "Operating expenses", ...yearProj.map(p => `(${fmtMoneyFull(p.opex, sym)})`)],
      [lang === "es" ? "Marketing" : "Marketing", ...yearProj.map(p => `(${fmtMoneyFull(p.marketing, sym)})`)],
      [
        { content: "EBITDA", styles: { fontStyle: "bold" as const, fillColor: SOFT_BG } },
        ...yearProj.map(p => ({
          content: fmtMoneyFull(p.ebitda, sym),
          styles: {
            fontStyle: "bold" as const,
            fillColor: SOFT_BG,
            textColor: p.ebitda >= 0 ? GREEN : RED,
          },
        })),
      ],
      [
        { content: lang === "es" ? "Margen EBITDA" : "EBITDA margin", styles: { fontStyle: "bold" as const, fillColor: SOFT_BG, textColor: MUTED } },
        ...yearProj.map(p => ({
          content: pct((p.ebitda / p.rev) * 100),
          styles: { fontStyle: "bold" as const, fillColor: SOFT_BG, textColor: MUTED },
        })),
      ],
    ],
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9.5, halign: "center" },
    styles: { fontSize: 9.5, cellPadding: 3, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1, halign: "right" },
    columnStyles: { 0: { halign: "left", fontStyle: "bold", textColor: INK, cellWidth: 70 } },
  });
  y = (doc as any).lastAutoTable.finalY + 8;

  subHeader(lang === "es" ? "Ingresos Anuales" : "Annual Revenue");
  barChart([
    { label: "Year 1", value: yearProj[0].rev, color: PRIMARY },
    { label: "Year 2", value: yearProj[1].rev, color: PRIMARY },
    { label: "Year 3", value: yearProj[2].rev, color: PRIMARY },
  ], { height: 55 });

  // ────────────────────────────────────────────────────────
  // 7. STARTUP COST BREAKDOWN
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_startup);

  paragraph(
    overrides.startupCostsCommentary?.trim() ||
    (lang === "es"
      ? `El capital inicial total estimado asciende a ${fmtMoneyFull(results.totalEstimate, sym)}. El siguiente desglose muestra la asignación por categoría según referencias del sector y el tipo de negocio.`
      : `Total estimated startup capital is ${fmtMoneyFull(results.totalEstimate, sym)}. The breakdown below shows allocation by category, derived from industry benchmarks and the selected business type.`),
  );

  donutChart(
    results.costBreakdown.map(c => ({ label: c.category, value: c.estimate })),
    { size: 50 },
  );

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      lang === "es" ? "Categoría" : "Category",
      lang === "es" ? "Monto" : "Amount",
      "%",
    ]],
    body: [
      ...results.costBreakdown.map(c => [
        c.category,
        fmtMoneyFull(c.estimate, sym),
        `${c.percentage}%`,
      ]),
      [
        { content: t.totalLabel, styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: fmtMoneyFull(results.totalEstimate, sym), styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: "100%", styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
      ],
    ],
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9.5 },
    styles: { fontSize: 9.5, cellPadding: 3, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1 },
    alternateRowStyles: { fillColor: SOFT_BG },
    columnStyles: {
      0: { cellWidth: 80, fontStyle: "bold", textColor: INK },
      1: { halign: "right", cellWidth: 50 },
      2: { halign: "right", cellWidth: 30 },
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // ────────────────────────────────────────────────────────
  // 8. MONTHLY P&L FORECAST — Year 1
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_pnl);

  paragraph(
    overrides.pnlCommentary?.trim() ||
    (lang === "es"
      ? `Pronóstico mensual del Año 1 mostrando la rampa hasta el funcionamiento estabilizado. El break-even se proyecta entre los meses 6 y 9 según la velocidad de adquisición de clientes.`
      : `Year 1 monthly forecast showing the ramp to stabilized run-rate. Break-even is projected between months 6 and 9 depending on customer acquisition pace.`),
  );

  // Build month-by-month: revenue ramps from 50% to 100% of monthly base over 12 months
  const months = lang === "es"
    ? ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"]
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const monthlyRows = months.map((m, i) => {
    const rampFactor = 0.5 + (0.5 * i) / 11; // 0.5 → 1.0
    const rev = baseMonthlyRevenue * rampFactor;
    const cogs = rev * cogsPct;
    const labor = monthlyLabor * (i < 3 ? 0.85 : 1.0);
    const rent = baseMonthlyRevenue * rentPct;
    const opex = rev * opexPct;
    const marketing = rev * marketingPct;
    const net = rev - cogs - labor - rent - opex - marketing;
    return { m, rev, cogs, labor, rent, opex, marketing, net };
  });

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      t.month,
      lang === "es" ? "Ingresos" : "Revenue",
      "COGS",
      lang === "es" ? "Personal" : "Labor",
      lang === "es" ? "Renta" : "Rent",
      "Opex",
      "Mkt",
      lang === "es" ? "Neto" : "Net",
    ]],
    body: monthlyRows.map(r => [
      r.m,
      fmtMoney(r.rev, sym),
      fmtMoney(r.cogs, sym),
      fmtMoney(r.labor, sym),
      fmtMoney(r.rent, sym),
      fmtMoney(r.opex, sym),
      fmtMoney(r.marketing, sym),
      {
        content: fmtMoney(r.net, sym),
        styles: { fontStyle: "bold" as const, textColor: r.net >= 0 ? GREEN : RED },
      },
    ]),
    foot: [[
      { content: t.totalLabel, styles: { fontStyle: "bold" as const } },
      { content: fmtMoney(monthlyRows.reduce((s, r) => s + r.rev, 0), sym), styles: { fontStyle: "bold" as const } },
      { content: fmtMoney(monthlyRows.reduce((s, r) => s + r.cogs, 0), sym), styles: { fontStyle: "bold" as const } },
      { content: fmtMoney(monthlyRows.reduce((s, r) => s + r.labor, 0), sym), styles: { fontStyle: "bold" as const } },
      { content: fmtMoney(monthlyRows.reduce((s, r) => s + r.rent, 0), sym), styles: { fontStyle: "bold" as const } },
      { content: fmtMoney(monthlyRows.reduce((s, r) => s + r.opex, 0), sym), styles: { fontStyle: "bold" as const } },
      { content: fmtMoney(monthlyRows.reduce((s, r) => s + r.marketing, 0), sym), styles: { fontStyle: "bold" as const } },
      {
        content: fmtMoney(monthlyRows.reduce((s, r) => s + r.net, 0), sym),
        styles: { fontStyle: "bold" as const, textColor: monthlyRows.reduce((s, r) => s + r.net, 0) >= 0 ? GREEN : RED },
      },
    ]],
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5, halign: "center" },
    footStyles: { fillColor: SOFT_BG, textColor: INK, fontSize: 8.5, halign: "right" },
    styles: { fontSize: 8.5, cellPadding: 2, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1, halign: "right" },
    columnStyles: { 0: { halign: "left", fontStyle: "bold", textColor: INK } },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // Monthly net trend chart
  subHeader(lang === "es" ? "Resultado Mensual Neto" : "Monthly Net Income Trend");
  barChart(
    monthlyRows.map(r => ({
      label: r.m,
      value: Math.max(0, r.net),
      color: r.net >= 0 ? GREEN : RED,
    })),
    { height: 48 },
  );

  // ────────────────────────────────────────────────────────
  // 9. STAFFING PLAN
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_staffing);

  paragraph(
    overrides.staffingCommentary?.trim() ||
    (lang === "es"
      ? `El plan de personal cubre los roles esenciales para operar de forma segura y eficiente. Las tarifas mensuales se basan en referencias del mercado local y suposiciones estándar de horas (40h/semana para tiempo completo).`
      : `The staffing plan covers all roles required for safe and efficient operations. Monthly rates are based on local market benchmarks and standard hour assumptions (40 hrs/week for full-time roles).`),
  );

  const totalAnnualLabor = results.totalMonthlyLabor * 12;
  kpiCards([
    { label: "Headcount", value: `${results.staffing.length}` },
    { label: "Monthly Labor", value: fmtMoney(results.totalMonthlyLabor, sym) },
    { label: "Annual Labor", value: fmtMoney(totalAnnualLabor, sym) },
    { label: "% of Y1 Revenue", value: pct((totalAnnualLabor / yearProj[0].rev) * 100) },
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      lang === "es" ? "Rol" : "Role",
      lang === "es" ? "Cant." : "Count",
      lang === "es" ? "Tarifa" : "Hourly",
      lang === "es" ? "Mensual" : "Monthly",
      lang === "es" ? "Anual" : "Annual",
      lang === "es" ? "Prioridad" : "Priority",
    ]],
    body: [
      ...results.staffing.map(s => [
        s.role,
        `${s.count}`,
        s.hourlyRate,
        s.monthlyEstimate > 0 ? fmtMoneyFull(s.monthlyEstimate, sym) : "—",
        s.monthlyEstimate > 0 ? fmtMoneyFull(s.monthlyEstimate * 12, sym) : "—",
        s.priority,
      ]),
      [
        { content: t.totalLabel, styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: `${results.staffing.reduce((s, r) => s + r.count, 0)}`, styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: "—", styles: { fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: fmtMoneyFull(results.totalMonthlyLabor, sym), styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: fmtMoneyFull(totalAnnualLabor, sym), styles: { fontStyle: "bold" as const, fillColor: INK, textColor: [255, 255, 255] as any } },
        { content: "", styles: { fillColor: INK, textColor: [255, 255, 255] as any } },
      ],
    ],
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 9, cellPadding: 2.8, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1 },
    alternateRowStyles: { fillColor: SOFT_BG },
    columnStyles: {
      0: { fontStyle: "bold", textColor: INK },
      1: { halign: "center" },
      3: { halign: "right" },
      4: { halign: "right" },
    },
  });
  y = (doc as any).lastAutoTable.finalY + 4;

  // ────────────────────────────────────────────────────────
  // 10. PERMITS & COMPLIANCE
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_permits);

  paragraph(
    overrides.permitsCommentary?.trim() ||
    (lang === "es"
      ? `Lista regulatoria para ${formatCityName(results.city)}. Cada permiso incluye su tiempo estimado de procesamiento y costo de referencia. Confirme con cada autoridad antes de presupuestar.`
      : `Regulatory checklist for ${formatCityName(results.city)}. Each permit includes its estimated processing time and reference cost. Confirm requirements with each issuing authority before finalizing budgets.`),
  );

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      lang === "es" ? "Permiso / Licencia" : "Permit / License",
      lang === "es" ? "Requerido" : "Required",
      lang === "es" ? "Tiempo" : "Processing time",
      lang === "es" ? "Costo est." : "Est. cost",
    ]],
    body: results.permits.map(p => [
      p.name,
      p.required ? (lang === "es" ? "Sí" : "Yes") : (lang === "es" ? "Opcional" : "Optional"),
      p.processingTime,
      p.estimatedCost,
    ]),
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 8.5, cellPadding: 2.6, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1, overflow: "linebreak" },
    alternateRowStyles: { fillColor: SOFT_BG },
    columnStyles: {
      0: { fontStyle: "bold", textColor: INK, cellWidth: 65 },
      1: { halign: "center", cellWidth: 22 },
      2: { cellWidth: 38 },
    },
    didParseCell: data => {
      if (data.column.index === 1 && data.section === "body") {
        const required = data.cell.raw === "Yes" || data.cell.raw === "Sí";
        data.cell.styles.textColor = required ? GREEN : MUTED;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // ────────────────────────────────────────────────────────
  // 11. TIMELINE TO OPENING
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_timeline);

  paragraph(
    overrides.timelineCommentary?.trim() ||
    (lang === "es"
      ? `Cronograma estructurado por fases hasta la apertura. Las dependencias críticas se agrupan al inicio del proceso para evitar retrasos en cascada.`
      : `Phased timeline through opening day. Critical-path dependencies are front-loaded to prevent cascading delays.`),
  );

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      lang === "es" ? "Fase" : "Phase",
      lang === "es" ? "Duración" : "Duration",
      lang === "es" ? "Actividades clave" : "Key activities",
    ]],
    body: results.timelineSteps.map(s => [
      s.step,
      s.duration,
      s.substeps.map(sub => `• ${sub.task}`).join("\n"),
    ]),
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 8.5, cellPadding: 2.6, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1, overflow: "linebreak" },
    alternateRowStyles: { fillColor: SOFT_BG },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: "bold", textColor: INK },
      1: { cellWidth: 25, halign: "center" },
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  if (results.milestones?.length) {
    subHeader(lang === "es" ? "Hitos Críticos" : "Critical Milestones");
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[
        lang === "es" ? "Hito" : "Milestone",
        lang === "es" ? "Semanas antes" : "Weeks before opening",
        lang === "es" ? "Crítico" : "Critical",
      ]],
      body: results.milestones.map(mi => [
        mi.label,
        mi.weeksBeforeOpen === 0 ? (lang === "es" ? "Día 0" : "Day 0") : `${mi.weeksBeforeOpen}`,
        mi.critical ? (lang === "es" ? "Sí" : "Yes") : "—",
      ]),
      theme: "grid",
      headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 2.6, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1 },
      alternateRowStyles: { fillColor: SOFT_BG },
      columnStyles: {
        0: { fontStyle: "bold", textColor: INK },
        1: { halign: "center", cellWidth: 45 },
        2: { halign: "center", cellWidth: 25 },
      },
    });
    y = (doc as any).lastAutoTable.finalY + 4;
  }

  // ────────────────────────────────────────────────────────
  // 12. RISK ANALYSIS
  // ────────────────────────────────────────────────────────
  startSectionPage();
  sectionHeader(t.s_risk);

  paragraph(
    overrides.riskCommentary?.trim() ||
    (lang === "es"
      ? `Identificación de los principales riesgos del proyecto y estrategias de mitigación. La probabilidad e impacto se evalúan según el patrón histórico del sector.`
      : `Identification of key project risks and mitigation strategies. Likelihood and impact are scored against historical industry patterns.`),
  );

  const risks = [
    {
      risk: lang === "es" ? "Sobrecostes en construcción/buildout" : "Construction & buildout cost overruns",
      likelihood: lang === "es" ? "Alta" : "High",
      impact: lang === "es" ? "Alto" : "High",
      mitigation: lang === "es"
        ? "Reservar 15–20% de contingencia. Obtener 3 presupuestos. Contratos a precio fijo donde sea posible."
        : "Reserve a 15–20% contingency. Obtain 3 bids. Use fixed-price contracts where possible.",
    },
    {
      risk: lang === "es" ? "Retrasos en permisos y licencias" : "Permit & license delays",
      likelihood: lang === "es" ? "Alta" : "High",
      impact: lang === "es" ? "Medio" : "Medium",
      mitigation: lang === "es"
        ? "Iniciar todas las solicitudes 90+ días antes. Confirmar tiempos con cada autoridad."
        : "Start all applications 90+ days early. Confirm timelines directly with issuing authorities.",
    },
    {
      risk: lang === "es" ? "Adquisición de clientes más lenta de lo previsto" : "Slower-than-projected customer acquisition",
      likelihood: lang === "es" ? "Media" : "Medium",
      impact: lang === "es" ? "Alto" : "High",
      mitigation: lang === "es"
        ? "Mantener 6 meses de capital de trabajo. Plan de marketing escalonado con KPIs semanales."
        : "Maintain 6 months of working capital. Tiered marketing plan with weekly KPIs.",
    },
    {
      risk: lang === "es" ? "Costos laborales superiores a los previstos" : "Labor costs above plan",
      likelihood: lang === "es" ? "Media" : "Medium",
      impact: lang === "es" ? "Medio" : "Medium",
      mitigation: lang === "es"
        ? "Hibridar tiempo completo / parcial. Cross-training. Revisar costo laboral semanalmente."
        : "Blend full-time and part-time. Cross-train staff. Review labor cost weekly.",
    },
    {
      risk: lang === "es" ? "Inflación de insumos / interrupciones de suministro" : "Input inflation / supply disruptions",
      likelihood: lang === "es" ? "Media" : "Medium",
      impact: lang === "es" ? "Medio" : "Medium",
      mitigation: lang === "es"
        ? "Dos proveedores por categoría crítica. Contratos con precios bloqueados a 6 meses."
        : "Maintain two suppliers per critical category. Lock-in 6-month pricing contracts.",
    },
    {
      risk: lang === "es" ? "Cumplimiento de salud / seguridad" : "Health & safety compliance failure",
      likelihood: lang === "es" ? "Baja" : "Low",
      impact: lang === "es" ? "Alto" : "High",
      mitigation: lang === "es"
        ? "Manager certificado en seguridad alimentaria. Auditorías internas trimestrales."
        : "Certified food safety manager on staff. Quarterly internal audits.",
    },
    {
      risk: lang === "es" ? "Concentración de proveedor único" : "Single-vendor concentration",
      likelihood: lang === "es" ? "Baja" : "Low",
      impact: lang === "es" ? "Medio" : "Medium",
      mitigation: lang === "es"
        ? "Diversificar proveedores y mantener inventario de seguridad."
        : "Diversify vendors and keep safety stock for high-velocity SKUs.",
    },
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: M, right: M },
    head: [[
      lang === "es" ? "Riesgo" : "Risk",
      lang === "es" ? "Prob." : "Likelihood",
      lang === "es" ? "Impacto" : "Impact",
      lang === "es" ? "Mitigación" : "Mitigation",
    ]],
    body: risks.map(r => [r.risk, r.likelihood, r.impact, r.mitigation]),
    theme: "grid",
    headStyles: { fillColor: INK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
    styles: { fontSize: 8.5, cellPadding: 2.8, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1, overflow: "linebreak" },
    alternateRowStyles: { fillColor: SOFT_BG },
    columnStyles: {
      0: { fontStyle: "bold", textColor: INK, cellWidth: 50 },
      1: { halign: "center", cellWidth: 22 },
      2: { halign: "center", cellWidth: 22 },
    },
    didParseCell: data => {
      if ((data.column.index === 1 || data.column.index === 2) && data.section === "body") {
        const v = String(data.cell.raw).toLowerCase();
        if (v.includes("high") || v.includes("alta") || v.includes("alto")) {
          data.cell.styles.textColor = RED;
        } else if (v.includes("med")) {
          data.cell.styles.textColor = ACCENT;
        } else {
          data.cell.styles.textColor = GREEN;
        }
        data.cell.styles.fontStyle = "bold";
      }
    },
  });
  y = (doc as any).lastAutoTable.finalY + 6;

  // ────────────────────────────────────────────────────────
  // 13. APPENDIX — DETAILED STARTUP BUDGET
  // ────────────────────────────────────────────────────────
  if (businessType) {
    const cats = getBudgetItemsForType(businessType);
    if (cats.length) {
      startSectionPage();
      sectionHeader(t.s_appendix);
      const { totalLow, totalHigh } = calculateBudgetTotals(cats);
      paragraph(
        lang === "es"
          ? `Desglose detallado por artículo. Rango total estimado: ${fmtMoneyFull(totalLow, sym)} – ${fmtMoneyFull(totalHigh, sym)}.`
          : `Itemized line-by-line breakdown. Total estimated range: ${fmtMoneyFull(totalLow, sym)} – ${fmtMoneyFull(totalHigh, sym)}.`,
      );

      for (const cat of cats) {
        checkPage(28);
        subHeader(cat.category);
        autoTable(doc, {
          startY: y,
          margin: { left: M, right: M },
          head: [[
            lang === "es" ? "Artículo" : "Item",
            lang === "es" ? "Bajo" : "Low",
            lang === "es" ? "Alto" : "High",
            t.notes,
          ]],
          body: cat.items.map(it => [
            it.item,
            it.lowEstimate === 0 && it.highEstimate === 0
              ? (lang === "es" ? "Gratis" : "Free")
              : fmtMoneyFull(it.lowEstimate, sym),
            it.lowEstimate === 0 && it.highEstimate === 0
              ? ""
              : fmtMoneyFull(it.highEstimate, sym),
            it.note,
          ]),
          theme: "grid",
          headStyles: { fillColor: SUBINK, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
          styles: { fontSize: 8.5, cellPadding: 2.4, textColor: SUBINK, lineColor: HAIR, lineWidth: 0.1, overflow: "linebreak" },
          alternateRowStyles: { fillColor: SOFT_BG },
          columnStyles: {
            0: { cellWidth: 55, fontStyle: "bold", textColor: INK },
            1: { halign: "right", cellWidth: 22 },
            2: { halign: "right", cellWidth: 22 },
          },
        });
        y = (doc as any).lastAutoTable.finalY + 5;
      }
    }
  }

  // ────────────────────────────────────────────────────────
  // Disclaimer
  // ────────────────────────────────────────────────────────
  checkPage(30);
  y += 6;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.2);
  doc.line(M, y, M + CW, y);
  y += 4;
  setFont(7.5, "normal", MUTED);
  doc.setFont("helvetica", "italic");
  const dlines = doc.splitTextToSize(t.disclaimer, CW);
  doc.text(dlines, M, y);

  // ────────────────────────────────────────────────────────
  // Backfill TOC
  // ────────────────────────────────────────────────────────
  doc.setPage(tocPageNumber);
  // Reset y just after the TOC heading rule
  let tocY = M + 14 + 18 + 4;
  setFont(10, "normal", SUBINK);
  toc.forEach(entry => {
    if (tocY > PAGE_H - 25) return; // safety; entries fit easily
    setFont(10.5, "bold", INK);
    doc.text(entry.title, M, tocY);
    setFont(10, "normal", MUTED);
    // dotted leader
    const titleW = doc.getTextWidth(entry.title);
    const pageStr = `${entry.page}`;
    const pageW = doc.getTextWidth(pageStr);
    const dotsStart = M + titleW + 3;
    const dotsEnd = M + CW - pageW - 3;
    if (dotsEnd > dotsStart) {
      doc.setDrawColor(...HAIR);
      doc.setLineDashPattern([0.6, 1.4], 0);
      doc.setLineWidth(0.3);
      doc.line(dotsStart, tocY - 1, dotsEnd, tocY - 1);
      doc.setLineDashPattern([], 0);
    }
    setFont(10, "bold", PRIMARY_DARK);
    doc.text(pageStr, M + CW, tocY, { align: "right" });
    tocY += 7.5;
  });

  // ────────────────────────────────────────────────────────
  // Page numbers — applied to every page except the cover (page 1)
  // ────────────────────────────────────────────────────────
  const total = doc.getNumberOfPages();
  for (let i = 2; i <= total; i++) {
    doc.setPage(i);
    setFont(7.5, "normal", MUTED);
    doc.text(t.confidential, M, PAGE_H - 8);
    doc.text(
      `${t.page} ${i} ${t.of} ${total}`,
      PAGE_W - M,
      PAGE_H - 8,
      { align: "right" },
    );
  }

  return doc;
}

// ────────────────────────────────────────────────────────────
// Public API — same signatures as before
// ────────────────────────────────────────────────────────────
export function generateBusinessPlanPDF(
  results: MockResults,
  lang: "en" | "es" = "en",
  businessType?: string,
  overrides: PlanOverrides = {},
) {
  try {
    const doc = buildPDF(results, lang, businessType, overrides);
    const filename = lang === "es"
      ? `Plan_Negocio_${results.businessLabel.replace(/\s+/g, "_")}_${results.city.replace(/\s+/g, "_")}.pdf`
      : `Business_Plan_${results.businessLabel.replace(/\s+/g, "_")}_${results.city.replace(/\s+/g, "_")}.pdf`;
    doc.save(filename);
  } catch (err) {
    console.error("PDF generation error:", err);
    try {
      const doc = buildPDF(results, lang, businessType, overrides);
      const blobUrl = doc.output("bloburl") as unknown as string;
      window.open(blobUrl, "_blank");
    } catch (e2) {
      console.error("PDF fallback failed:", e2);
    }
  }
}

export function generateBusinessPlanBlob(
  results: MockResults,
  lang: "en" | "es" = "en",
  businessType?: string,
  overrides: PlanOverrides = {},
): Blob {
  const doc = buildPDF(results, lang, businessType, overrides);
  return doc.output("blob");
}